import React, { useRef, useState } from 'react';
import { useImpression, useTracker } from '@archipro-website/tracker';
import { useABTestIsOn } from '~/modules/root/hook/use-growthbook';
import type { EnquiryProfessional } from '~/modules/enquiry/component/enquiry-form/EnquiryForm';
import { EnquiryForm } from '~/modules/enquiry/component/enquiry-form/EnquiryForm';
import LoginSignUpModal from '~/modules/user/component/login-signup-modal/LoginSignUpModal';
import { usePublicUser } from '~/modules/user/hook/use-public-user';
import type { FavouriteItem } from '~/modules/design-board/type';
import { trackEnquiryEngagement } from '~/modules/tracking/util/trackEnquiryEngagement';
import type { EnquiryItemType } from '~/modules/tracking/util/trackEnquireReveal';
import { trackEnquireReveal } from '~/modules/tracking/util/trackEnquireReveal';
import type { UseEnquiryProps } from '@modules-rocco/enquiry/hooks/use-enquiry';
import { EnquiryForm as NewEnquiryForm } from '~/ui-rocco/enquiry/EnquiryForm';

interface EnquiryInlineProps {
    className?: string;
    /** The professional to submit an enquiry to. */
    professional: EnquiryProfessional;
    /** The ID of the directory item (professional, product, project, article). */
    siteTreeID: number;
    /** The type of item to enquire about. */
    itemType: EnquiryItemType;
    /** The subject of the enquiry (product, project, article). */
    relatedItem?: UseEnquiryProps['relatedItem'];
    /** Include the branch dropdown in the enquiry form if the professional has branches. */
    includeBranch?: boolean;
    /** The favourite item to save to design board. */
    favouriteItem?: FavouriteItem;
    /** The text to show on the submit button. */
    submitButtonText?: string;
    /** Custom message body placeholder text (old version). */
    messageBodyPlaceholder?: string;
    /** Show custom enquiry text from professional settings. */
    showCustomEnquiryText?: boolean;
    /** Disable the submit button. */
    disableSubmit?: boolean;
    /** Disable the save to design board button (old version). */
    disableSaveToDesignBoard?: boolean;
}

/**
 * Inline (in-page) version of the enquiry form.
 */
export const EnquiryInline = ({
    className,
    professional,
    siteTreeID,
    itemType,
    relatedItem,
    includeBranch = false,
    favouriteItem,
    submitButtonText,
    messageBodyPlaceholder,
    showCustomEnquiryText = true,
    disableSubmit = false,
    disableSaveToDesignBoard = false,
}: EnquiryInlineProps) => {
    const tracker = useTracker();
    const { publicUser, setPublicUser } = usePublicUser('enquiry');
    const isNewEnquiryForm = useABTestIsOn('new-enquiry-form');
    const [loginModalOpen, setLoginModalOpen] = useState(false);
    const formRef = useRef<HTMLDivElement>(null);
    const isEngagementTracked = useRef(false);

    useImpression({
        ref: formRef,
        onImpression: async () => {
            trackEnquireReveal(tracker, {
                professionalName: professional.Title,
                itemID: siteTreeID,
                itemType,
                source: 'InPage',
            });
        },
    });

    const trackEngagement = (e: React.FocusEvent<HTMLDivElement>) => {
        if (
            e.target instanceof HTMLInputElement ||
            e.target instanceof HTMLTextAreaElement ||
            e.target instanceof HTMLSelectElement
        ) {
            // Only track engagement once.
            if (!isEngagementTracked.current) {
                trackEnquiryEngagement(tracker, {
                    professional: {
                        ID: professional.ID,
                        Title: professional.Title,
                    },
                    itemID: siteTreeID,
                    itemType,
                    source: 'page',
                });
                isEngagementTracked.current = true;
            }
        }
    };
    return (
        <div ref={formRef} className={className} onFocus={trackEngagement}>
            {isNewEnquiryForm ? (
                <NewEnquiryForm
                    professional={professional}
                    siteTreeID={siteTreeID}
                    source="InPage"
                    relatedItem={relatedItem}
                    includeBranch={includeBranch}
                    showCustomEnquiryMessage={showCustomEnquiryText}
                    submitButtonText={submitButtonText}
                    disableSubmit={disableSubmit}
                    showSuccessMessage
                    onSuccess={() => {
                        setLoginModalOpen(false);
                    }}
                    onFailure={(publicUser) => {
                        setPublicUser(publicUser);
                        setLoginModalOpen(true);
                    }}
                />
            ) : (
                <>
                    {showCustomEnquiryText &&
                        professional.CustomEnquiryMessage && (
                            <div className="mb-4 whitespace-pre-wrap text-base">
                                {professional.CustomEnquiryMessage}
                            </div>
                        )}
                    <EnquiryForm
                        professional={professional}
                        branchOptions={
                            includeBranch
                                ? (professional.branchOptions ?? [])
                                : []
                        }
                        disableSubmit={disableSubmit}
                        disableSaveToDesignBoard={disableSaveToDesignBoard}
                        onSuccess={() => {
                            setLoginModalOpen(false);
                        }}
                        onFailure={(publicUser) => {
                            setPublicUser(publicUser);
                            setLoginModalOpen(true);
                        }}
                        source={'InPage'}
                        submitButtonText={submitButtonText}
                        messageBodyPlaceholder={messageBodyPlaceholder}
                        favouriteItem={favouriteItem}
                        siteTreeID={siteTreeID}
                        relatedItem={relatedItem}
                    />
                </>
            )}
            {publicUser && (
                <LoginSignUpModal
                    login={{
                        open: loginModalOpen,
                        onCancel: () => {
                            setLoginModalOpen(false);
                        },
                        onConfirm: () => setLoginModalOpen(false),
                        flow: 'post-enquiry-login',
                        defaultPublicUser: publicUser,
                        authSource: 'enquiry',
                        onAuthSuccess: () => setLoginModalOpen(false),
                    }}
                    forgotPassword={{
                        open: loginModalOpen,
                        onCancel: () => setLoginModalOpen(false),
                        onConfirm: () => setLoginModalOpen(false),
                    }}
                />
            )}
        </div>
    );
};
